import { useMemo, type ReactNode } from 'react';
import { GeometryEntity, GeometryLabel, GeometryLesson, GeometryMarker, Problem, Step } from '../../types/tutor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { InteractiveCheckpoint } from './InteractiveCheckpoint';
import { API_HOST } from '@/lib/config';

function resolveMarkdownImageSrc(src: string | undefined): string | undefined {
  if (!src) return src
  const trimmed = src.trim()
  if (!trimmed) return trimmed
  
  // Filter out invalid image links (e.g., question-UUID without extension)
  // These are leftover from original data where images were already embedded via ![](xxx.jpg)
  if (/^question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?$/i.test(trimmed)) {
    return undefined
  }
  
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) return trimmed

  const cleaned = trimmed.replace(/^\.\/+/, "")
  if (cleaned.startsWith("/images/")) {
    const path = `/media/qb_images${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }
  // Fix stale paths with extra "images/" directory (e.g. /media/qb_images/images/{hash}.jpg → /media/qb_images/{hash}.jpg)
  const qbImagesMatch = cleaned.match(/^\/media\/qb_images\/images\/([0-9a-f]{64}\.(png|jpg|jpeg|webp|gif))$/i)
  if (qbImagesMatch) {
    const path = `/media/qb_images/${qbImagesMatch[1]}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }
  if (cleaned.startsWith("/")) {
    if (!API_HOST) return cleaned
    return `${API_HOST}${cleaned}`
  }

  if (/^[0-9a-f]{64}\.(png|jpg|jpeg|webp|gif)$/i.test(cleaned)) {
    const path = `/media/qb_images/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  if (cleaned.startsWith("media/")) {
    const path = `/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  if (cleaned.startsWith("qb_images/")) {
    const path = `/media/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  if (cleaned.startsWith("images/")) {
    const path = `/media/qb_images/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  return trimmed
}

interface StepViewerProps {
  problem: Problem;
  currentStep: Step;
  historySteps: Step[];
  mode?: "generic" | "geometry";
  geometryLesson?: GeometryLesson;
  className?: string;
}

const SUPPORTED_ACTIONS = new Set([
  'show',
  'hide',
  'fade_in',
  'fade_out',
  'highlight',
  'dim',
  'set_style',
  'set_state',
  'add_entity',
  'remove_entity',
  'add_marker',
  'remove_marker',
  'add_label',
  'remove_label',
  'caption',
  'callout',
  'tooltip',
  'camera_focus',
  'camera_reset',
  'wait'
]);

type SceneMaps = {
  entities: Map<string, GeometryEntity>;
  labels: Map<string, GeometryLabel>;
  markers: Map<string, GeometryMarker>;
  degradedByStep: Record<string, number>;
  reports: string[];
};

function toBoolean(value: unknown, defaultValue = true) {
  return typeof value === 'boolean' ? value : defaultValue;
}

function buildInitialScene(geometryLesson?: GeometryLesson): SceneMaps {
  const entities = new Map<string, GeometryEntity>();
  const labels = new Map<string, GeometryLabel>();
  const markers = new Map<string, GeometryMarker>();
  const rawEntities = geometryLesson?.scene?.entities ?? [];
  const rawLabels = geometryLesson?.scene?.labels ?? [];
  const rawMarkers = geometryLesson?.scene?.markers ?? [];
  rawEntities.forEach((entity) => {
    entities.set(entity.id, { ...entity, visible: toBoolean(entity.visible, true), state: typeof entity.state === 'string' ? entity.state : 'default' });
  });
  rawLabels.forEach((label) => {
    labels.set(label.id, { ...label, visible: toBoolean(label.visible, true) });
  });
  rawMarkers.forEach((marker) => {
    markers.set(marker.id, { ...marker, visible: toBoolean(marker.visible, true) });
  });
  return { entities, labels, markers, degradedByStep: {}, reports: [] };
}

function markDegraded(scene: SceneMaps, stepId: string, report: string) {
  scene.degradedByStep[stepId] = (scene.degradedByStep[stepId] ?? 0) + 1;
  scene.reports.push(report);
}

function applyVisibility(scene: SceneMaps, targetIds: string[], visible: boolean, stepId: string, actionType: string) {
  if (targetIds.length === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:empty_target`);
    return;
  }
  let hit = 0;
  targetIds.forEach((id) => {
    const entity = scene.entities.get(id);
    if (entity) {
      scene.entities.set(id, { ...entity, visible });
      hit += 1;
      return;
    }
    const label = scene.labels.get(id);
    if (label) {
      scene.labels.set(id, { ...label, visible });
      hit += 1;
      return;
    }
    const marker = scene.markers.get(id);
    if (marker) {
      scene.markers.set(id, { ...marker, visible });
      hit += 1;
    }
  });
  if (hit === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:target_missing`);
  }
}

function applyState(scene: SceneMaps, targetIds: string[], state: string, stepId: string, actionType: string) {
  if (targetIds.length === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:empty_target`);
    return;
  }
  let hit = 0;
  targetIds.forEach((id) => {
    const entity = scene.entities.get(id);
    if (entity) {
      scene.entities.set(id, { ...entity, state });
      hit += 1;
    }
  });
  if (hit === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:target_missing`);
  }
}

function applyStyle(scene: SceneMaps, targetIds: string[], style: Record<string, unknown>, stepId: string, actionType: string) {
  if (targetIds.length === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:empty_target`);
    return;
  }
  let hit = 0;
  targetIds.forEach((id) => {
    const entity = scene.entities.get(id);
    if (entity) {
      scene.entities.set(id, { ...entity, style: { ...(entity.style ?? {}), ...style } });
      hit += 1;
    }
  });
  if (hit === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:target_missing`);
  }
}

function applyAddObject(scene: SceneMaps, stepId: string, actionType: string, kind: 'entity' | 'label' | 'marker', targetIds: string[], payload: Record<string, unknown>) {
  const rawObject = payload.object;
  const objectData = (typeof rawObject === 'object' && rawObject !== null ? rawObject : undefined) as Record<string, unknown> | undefined;
  if (!objectData || typeof objectData.id !== 'string') {
    markDegraded(scene, stepId, `${stepId}:${actionType}:object_missing`);
    return;
  }
  const objectId = objectData.id;
  if (targetIds.length > 0 && !targetIds.includes(objectId)) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:target_mismatch`);
  }
  if (kind === 'entity') {
    const entity = objectData as unknown as GeometryEntity;
    scene.entities.set(objectId, { ...entity, visible: toBoolean(entity.visible, true), state: typeof entity.state === 'string' ? entity.state : 'default' });
    return;
  }
  if (kind === 'label') {
    const label = objectData as unknown as GeometryLabel;
    scene.labels.set(objectId, { ...label, visible: toBoolean(label.visible, true) });
    return;
  }
  const marker = objectData as unknown as GeometryMarker;
  scene.markers.set(objectId, { ...marker, visible: toBoolean(marker.visible, true) });
}

function applyRemoveObject(scene: SceneMaps, stepId: string, actionType: string, kind: 'entity' | 'label' | 'marker', targetIds: string[]) {
  if (targetIds.length === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:empty_target`);
    return;
  }
  let removed = 0;
  targetIds.forEach((id) => {
    if (kind === 'entity' && scene.entities.delete(id)) {
      removed += 1;
      return;
    }
    if (kind === 'label' && scene.labels.delete(id)) {
      removed += 1;
      return;
    }
    if (kind === 'marker' && scene.markers.delete(id)) {
      removed += 1;
    }
  });
  if (removed === 0) {
    markDegraded(scene, stepId, `${stepId}:${actionType}:target_missing`);
  }
}

function applyActionsForStep(scene: SceneMaps, stepId: string, actions: GeometryLesson['timeline']['steps'][number]['actions']) {
  actions.forEach((action) => {
    if (!SUPPORTED_ACTIONS.has(action.type)) {
      markDegraded(scene, stepId, `${stepId}:${action.type}:unsupported`);
      return;
    }
    const targetIds = Array.isArray(action.target_ids) ? action.target_ids : [];
    if (action.type === 'wait') {
      return;
    }
    if (action.type === 'show' || action.type === 'fade_in') {
      applyVisibility(scene, targetIds, true, stepId, action.type);
      return;
    }
    if (action.type === 'hide' || action.type === 'fade_out') {
      applyVisibility(scene, targetIds, false, stepId, action.type);
      return;
    }
    if (action.type === 'highlight') {
      applyState(scene, targetIds, 'highlighted', stepId, action.type);
      return;
    }
    if (action.type === 'dim') {
      applyState(scene, targetIds, 'dimmed', stepId, action.type);
      return;
    }
    if (action.type === 'set_state') {
      const payloadState = typeof action.payload?.state === 'string' ? action.payload.state : 'focused';
      applyState(scene, targetIds, payloadState, stepId, action.type);
      return;
    }
    if (action.type === 'set_style') {
      const style = (typeof action.payload?.style === 'object' && action.payload?.style !== null ? action.payload.style : {}) as Record<string, unknown>;
      applyStyle(scene, targetIds, style, stepId, action.type);
      return;
    }
    if (action.type === 'add_entity') {
      applyAddObject(scene, stepId, action.type, 'entity', targetIds, action.payload ?? {});
      return;
    }
    if (action.type === 'remove_entity') {
      applyRemoveObject(scene, stepId, action.type, 'entity', targetIds);
      return;
    }
    if (action.type === 'add_label') {
      applyAddObject(scene, stepId, action.type, 'label', targetIds, action.payload ?? {});
      return;
    }
    if (action.type === 'remove_label') {
      applyRemoveObject(scene, stepId, action.type, 'label', targetIds);
      return;
    }
    if (action.type === 'add_marker') {
      applyAddObject(scene, stepId, action.type, 'marker', targetIds, action.payload ?? {});
      return;
    }
    if (action.type === 'remove_marker') {
      applyRemoveObject(scene, stepId, action.type, 'marker', targetIds);
      return;
    }
    if (action.type === 'caption' || action.type === 'callout' || action.type === 'tooltip' || action.type === 'camera_focus' || action.type === 'camera_reset') {
      return;
    }
    markDegraded(scene, stepId, `${stepId}:${action.type}:execution_failed`);
  });
}

function applyFallbackSnapshot(scene: SceneMaps, geometryLesson?: GeometryLesson) {
  const fallbackMode = geometryLesson?.fallback?.mode;
  if (!fallbackMode || fallbackMode === 'none') {
    return;
  }
  scene.entities.forEach((entity, id) => {
    scene.entities.set(id, { ...entity, visible: false });
  });
  scene.labels.forEach((label, id) => {
    scene.labels.set(id, { ...label, visible: false });
  });
  scene.markers.forEach((marker, id) => {
    scene.markers.set(id, { ...marker, visible: false });
  });
  const showEntities = geometryLesson?.fallback?.static_snapshot?.show_entities ?? [];
  showEntities.forEach((id) => {
    const entity = scene.entities.get(id);
    if (entity) {
      scene.entities.set(id, { ...entity, visible: true });
      return;
    }
    const label = scene.labels.get(id);
    if (label) {
      scene.labels.set(id, { ...label, visible: true });
      return;
    }
    const marker = scene.markers.get(id);
    if (marker) {
      scene.markers.set(id, { ...marker, visible: true });
    }
  });
}

function stateStyle(entity: GeometryEntity) {
  const state = entity.state ?? 'default';
  const style = entity.style ?? {};
  const styleStroke = typeof style.stroke === 'string' ? style.stroke : undefined;
  const styleFill = typeof style.fill === 'string' ? style.fill : undefined;
  const styleOpacity = typeof style.opacity === 'number' ? style.opacity : undefined;
  const styleWidth = typeof style.width === 'number' ? style.width : undefined;
  if (styleStroke || styleFill || styleOpacity !== undefined || styleWidth !== undefined) {
    return {
      stroke: styleStroke ?? '#334155',
      fill: styleFill ?? styleStroke ?? '#334155',
      opacity: styleOpacity ?? 1,
      width: styleWidth ?? 2
    };
  }
  if (state === 'highlighted') {
    return { stroke: '#f59e0b', fill: '#f59e0b', opacity: 1, width: 3 };
  }
  if (state === 'focused') {
    return { stroke: '#2563eb', fill: '#2563eb', opacity: 1, width: 3 };
  }
  if (state === 'proven') {
    return { stroke: '#16a34a', fill: '#16a34a', opacity: 1, width: 3 };
  }
  if (state === 'dimmed') {
    return { stroke: '#64748b', fill: '#64748b', opacity: 0.35, width: 2 };
  }
  return { stroke: '#334155', fill: '#334155', opacity: 1, width: 2 };
}

function labelPosition(label: GeometryLabel, entities: Map<string, GeometryEntity>) {
  const target = label.target_id ? entities.get(label.target_id) : undefined;
  if (target && target.type === 'point' && typeof target.x === 'number' && typeof target.y === 'number') {
    const dx = label.position === 'right' ? 10 : label.position === 'left' ? -14 : 8;
    const dy = label.position === 'bottom' ? 14 : -8;
    return { x: target.x + dx, y: target.y + dy };
  }
  if (target && target.type === 'segment' && typeof target.from === 'string' && typeof target.to === 'string') {
    const from = entities.get(target.from);
    const to = entities.get(target.to);
    if (from && to && typeof from.x === 'number' && typeof from.y === 'number' && typeof to.x === 'number' && typeof to.y === 'number') {
      return { x: (from.x + to.x) / 2 + 8, y: (from.y + to.y) / 2 - 6 };
    }
  }
  return { x: 16, y: 20 };
}

function getPoint(entityId: string, entities: Map<string, GeometryEntity>) {
  const entity = entities.get(entityId);
  if (!entity || entity.type !== 'point' || typeof entity.x !== 'number' || typeof entity.y !== 'number') {
    return null;
  }
  return { x: entity.x, y: entity.y };
}

function getSegmentEnds(segmentId: string, entities: Map<string, GeometryEntity>) {
  const segment = entities.get(segmentId);
  if (!segment || typeof segment.from !== 'string' || typeof segment.to !== 'string') {
    return null;
  }
  const from = getPoint(segment.from, entities);
  const to = getPoint(segment.to, entities);
  if (!from || !to) {
    return null;
  }
  return { from, to };
}

function segmentCenter(segmentId: string, entities: Map<string, GeometryEntity>) {
  const ends = getSegmentEnds(segmentId, entities);
  if (!ends) {
    return null;
  }
  return { x: (ends.from.x + ends.to.x) / 2, y: (ends.from.y + ends.to.y) / 2 };
}

function segmentDirection(segmentId: string, entities: Map<string, GeometryEntity>) {
  const ends = getSegmentEnds(segmentId, entities);
  if (!ends) {
    return null;
  }
  const dx = ends.to.x - ends.from.x;
  const dy = ends.to.y - ends.from.y;
  const norm = Math.hypot(dx, dy);
  if (norm < 1e-6) {
    return null;
  }
  return { ux: dx / norm, uy: dy / norm };
}

function segmentOtherEnd(segmentId: string, pivotId: string, entities: Map<string, GeometryEntity>) {
  const segment = entities.get(segmentId);
  if (!segment || typeof segment.from !== 'string' || typeof segment.to !== 'string') {
    return null;
  }
  if (segment.from === pivotId) {
    return getPoint(segment.to, entities);
  }
  if (segment.to === pivotId) {
    return getPoint(segment.from, entities);
  }
  return null;
}

function segmentVectorFromPivot(segmentId: string, pivotId: string, entities: Map<string, GeometryEntity>) {
  const segment = entities.get(segmentId);
  if (!segment || typeof segment.from !== 'string' || typeof segment.to !== 'string') {
    return null;
  }
  const pivot = getPoint(pivotId, entities);
  if (!pivot) {
    return null;
  }
  const otherId = segment.from === pivotId ? segment.to : segment.to === pivotId ? segment.from : null;
  if (!otherId) {
    return null;
  }
  const other = getPoint(otherId, entities);
  if (!other) {
    return null;
  }
  const dx = other.x - pivot.x;
  const dy = other.y - pivot.y;
  const norm = Math.hypot(dx, dy);
  if (norm < 1e-6) {
    return null;
  }
  return { ux: dx / norm, uy: dy / norm };
}

function markerCount(marker: GeometryMarker) {
  const raw = typeof marker.count === 'number' ? marker.count : 1;
  const safe = Number.isFinite(raw) ? Math.round(raw) : 1;
  return Math.min(Math.max(safe, 1), 4);
}

function markerStroke(marker: GeometryMarker) {
  const style = marker.style as Record<string, unknown> | undefined;
  return typeof style?.stroke === 'string' ? style.stroke : '#7c3aed';
}

function markerOpacity(marker: GeometryMarker) {
  const style = marker.style as Record<string, unknown> | undefined;
  const opacity = style?.opacity;
  return typeof opacity === 'number' ? opacity : 1;
}

function markerElements(marker: GeometryMarker, entities: Map<string, GeometryEntity>) {
  const targets = Array.isArray(marker.targets) ? marker.targets : [];
  const stroke = markerStroke(marker);
  const opacity = markerOpacity(marker);
  if (marker.type === 'equal_length') {
    const count = markerCount(marker);
    const bars: ReactNode[] = [];
    targets.forEach((targetId, targetIndex) => {
      const center = segmentCenter(targetId, entities);
      const direction = segmentDirection(targetId, entities);
      if (!center || !direction) {
        return;
      }
      const px = -direction.uy;
      const py = direction.ux;
      for (let i = 0; i < count; i += 1) {
        const along = (i - (count - 1) / 2) * 5;
        const cx = center.x + direction.ux * along;
        const cy = center.y + direction.uy * along;
        bars.push(
          <line
            key={`${marker.id}-equal-${targetIndex}-${i}`}
            x1={cx - px * 6}
            y1={cy - py * 6}
            x2={cx + px * 6}
            y2={cy + py * 6}
            stroke={stroke}
            strokeWidth={2}
            opacity={opacity}
          />
        );
      }
    });
    return bars;
  }
  if (marker.type === 'right_angle') {
    if (typeof marker.at !== 'string') {
      return [];
    }
    const at = getPoint(marker.at, entities);
    if (!at) {
      return [];
    }
    return [
      <path
        key={`${marker.id}-right-angle`}
        d={`M ${at.x} ${at.y - 10} L ${at.x + 10} ${at.y - 10} L ${at.x + 10} ${at.y}`}
        stroke={stroke}
        strokeWidth={2}
        fill="none"
        opacity={opacity}
      />
    ];
  }
  if (marker.type === 'equal_angle') {
    if (typeof marker.at !== 'string' || targets.length < 2) {
      return [];
    }
    const at = getPoint(marker.at, entities);
    if (!at) {
      return [];
    }
    const p1 = segmentOtherEnd(targets[0], marker.at, entities);
    const p2 = segmentOtherEnd(targets[1], marker.at, entities);
    if (!p1 || !p2) {
      return [];
    }
    const a1 = Math.atan2(p1.y - at.y, p1.x - at.x);
    const a2 = Math.atan2(p2.y - at.y, p2.x - at.x);
    let delta = a2 - a1;
    while (delta <= -Math.PI) delta += Math.PI * 2;
    while (delta > Math.PI) delta -= Math.PI * 2;
    const start = a1;
    const end = a1 + delta;
    const r = 18;
    const x1 = at.x + r * Math.cos(start);
    const y1 = at.y + r * Math.sin(start);
    const x2 = at.x + r * Math.cos(end);
    const y2 = at.y + r * Math.sin(end);
    const largeArc = Math.abs(delta) > Math.PI ? 1 : 0;
    const sweep = delta >= 0 ? 1 : 0;
    return [
      <path
        key={`${marker.id}-equal-angle`}
        d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`}
        stroke={stroke}
        strokeWidth={2}
        fill="none"
        opacity={opacity}
      />
    ];
  }
  if (marker.type === 'parallel') {
    const shapes: ReactNode[] = [];
    targets.forEach((targetId, index) => {
      const center = segmentCenter(targetId, entities);
      const direction = segmentDirection(targetId, entities);
      if (!center || !direction) {
        return;
      }
      const px = -direction.uy;
      const py = direction.ux;
      const baseOffset = index % 2 === 0 ? -4 : 4;
      const cx = center.x + px * baseOffset;
      const cy = center.y + py * baseOffset;
      const len = 7;
      const tick1 = (
        <line
          key={`${marker.id}-parallel-${index}-1`}
          x1={cx - direction.ux * len}
          y1={cy - direction.uy * len}
          x2={cx + direction.ux * len}
          y2={cy + direction.uy * len}
          stroke={stroke}
          strokeWidth={2}
          opacity={opacity}
        />
      );
      const tick2 = (
        <line
          key={`${marker.id}-parallel-${index}-2`}
          x1={cx - direction.ux * len + px * 4}
          y1={cy - direction.uy * len + py * 4}
          x2={cx + direction.ux * len + px * 4}
          y2={cy + direction.uy * len + py * 4}
          stroke={stroke}
          strokeWidth={2}
          opacity={opacity}
        />
      );
      shapes.push(tick1, tick2);
    });
    return shapes;
  }
  if (marker.type === 'midpoint') {
    if (targets.length === 0) {
      return [];
    }
    const center = segmentCenter(targets[0], entities);
    if (!center) {
      return [];
    }
    return [
      <circle
        key={`${marker.id}-midpoint`}
        cx={center.x}
        cy={center.y}
        r={4}
        fill={stroke}
        opacity={opacity}
      />
    ];
  }
  if (marker.type === 'perpendicular_badge') {
    if (typeof marker.at !== 'string' || targets.length < 2) {
      return [];
    }
    const at = getPoint(marker.at, entities);
    if (!at) {
      return [];
    }
    const v1 = segmentVectorFromPivot(targets[0], marker.at, entities);
    const v2 = segmentVectorFromPivot(targets[1], marker.at, entities);
    if (!v1 || !v2) {
      return [];
    }
    const size = 10;
    const p1 = { x: at.x + v1.ux * size, y: at.y + v1.uy * size };
    const p2 = { x: p1.x + v2.ux * size, y: p1.y + v2.uy * size };
    const p3 = { x: at.x + v2.ux * size, y: at.y + v2.uy * size };
    return [
      <path
        key={`${marker.id}-perpendicular`}
        d={`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y}`}
        stroke={stroke}
        strokeWidth={2}
        fill="none"
        opacity={opacity}
      />
    ];
  }
  if (marker.type === 'shaded_region') {
    const pointTargets = targets
      .map((id) => getPoint(id, entities))
      .filter((point): point is { x: number; y: number } => !!point);
    if (pointTargets.length < 3) {
      return [];
    }
    const points = pointTargets.map((point) => `${point.x},${point.y}`).join(' ');
    return [
      <polygon
        key={`${marker.id}-shaded-region`}
        points={points}
        fill={stroke}
        fillOpacity={0.2}
        stroke={stroke}
        strokeWidth={1}
        opacity={opacity}
      />
    ];
  }
  if (marker.type === 'focus_ring') {
    const firstTarget = targets[0];
    if (!firstTarget) {
      return [];
    }
    const point = getPoint(firstTarget, entities);
    if (!point) {
      return [];
    }
    return [
      <circle
        key={`${marker.id}-focus`}
        cx={point.x}
        cy={point.y}
        r={12}
        stroke={stroke}
        strokeWidth={2}
        fill="none"
        opacity={opacity}
      />
    ];
  }
  if (targets.length > 0) {
    const firstTargetCenter = segmentCenter(targets[0], entities) ?? getPoint(targets[0], entities);
    if (firstTargetCenter) {
      return [
        <circle
          key={`${marker.id}-fallback`}
          cx={firstTargetCenter.x}
          cy={firstTargetCenter.y}
          r={5}
          fill={stroke}
          opacity={opacity}
        />
      ];
    }
  }
  return [];
}

export function StepViewer({ problem, currentStep, historySteps, mode, geometryLesson, className }: StepViewerProps) {
  const currentVisualStep = geometryLesson?.timeline?.steps?.find((step) => step.id === currentStep.step_id);
  const fallbackMode = typeof geometryLesson?.fallback?.mode === 'string' ? geometryLesson.fallback.mode : 'none';
  const visualActions = currentVisualStep?.actions ?? [];
  const executableActions = visualActions.filter((action) => SUPPORTED_ACTIONS.has(action.type));
  const sceneSnapshot = useMemo(() => {
    const scene = buildInitialScene(geometryLesson);
    const currentPath = new Set([...historySteps.map((step) => step.step_id), currentStep.step_id]);
    const fallbackEnabled = fallbackMode !== 'none';
    if (!fallbackEnabled) {
      (geometryLesson?.timeline?.steps ?? []).forEach((step) => {
        if (!currentPath.has(step.id)) {
          return;
        }
        applyActionsForStep(scene, step.id, step.actions);
      });
    }
    applyFallbackSnapshot(scene, geometryLesson);
    return scene;
  }, [geometryLesson, historySteps, currentStep.step_id, fallbackMode]);
  const degradedActionCount = sceneSnapshot.degradedByStep[currentStep.step_id] ?? visualActions.length - executableActions.length;
  const stepRenderText = useMemo(() => {
    let text = currentVisualStep?.caption || currentStep.display_markdown;
    visualActions.forEach((action) => {
      if ((action.type === 'caption' || action.type === 'callout' || action.type === 'tooltip') && typeof action.payload?.text === 'string') {
        text = action.payload.text;
      }
    });
    return text;
  }, [currentVisualStep?.caption, currentStep.display_markdown, visualActions]);
  const visibleEntities = Array.from(sceneSnapshot.entities.values()).filter((entity) => entity.visible !== false);
  const visibleLabels = Array.from(sceneSnapshot.labels.values()).filter((label) => label.visible !== false);
  const visibleMarkers = Array.from(sceneSnapshot.markers.values()).filter((marker) => marker.visible !== false);
  const visiblePoints = visibleEntities.filter((entity) => entity.type === 'point' && typeof entity.x === 'number' && typeof entity.y === 'number');
  const visibleSegments = visibleEntities.filter((entity) => entity.type === 'segment' && typeof entity.from === 'string' && typeof entity.to === 'string');
  const markerRenderResult = useMemo(() => {
    const shapes: ReactNode[] = [];
    let degraded = 0;
    visibleMarkers.forEach((marker) => {
      const elements = markerElements(marker, sceneSnapshot.entities);
      if (elements.length === 0) {
        degraded += 1;
        return;
      }
      shapes.push(...elements);
    });
    return { shapes, degraded };
  }, [visibleMarkers, sceneSnapshot.entities]);
  const markerShapes = markerRenderResult.shapes;
  const markerDegradedCount = markerRenderResult.degraded;
  const markerRenderedCount = Math.max(visibleMarkers.length - markerDegradedCount, 0);
  const stepDegradedTotal = degradedActionCount + markerDegradedCount;
  const currentStepReports = sceneSnapshot.reports.filter((report) => report.startsWith(`${currentStep.step_id}:`)).slice(0, 2);

  return (
    <div className={cn("flex flex-col h-full overflow-y-auto bg-white p-6 md:p-8 space-y-8", className)}>
      <Card className="bg-slate-50 border-slate-200 shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-bold text-slate-800">题目描述</CardTitle>
            <div className="flex gap-2">
              {problem.asks.map((ask, i) => (
                <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                  {ask}
                </span>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="prose prose-slate max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex, rehypeRaw]}
              components={{
                img: ({node, ...props}) => (
                  <img
                    {...props}
                    src={resolveMarkdownImageSrc(props.src)}
                    className={cn("max-w-full h-auto inline-block", (props as any).className)}
                  />
                ),
                mark: ({node, ...props}) => {
                   const type = (props as any)['data-type'] as string;
                   const colors: Record<string, string> = {
                     key: 'bg-amber-100 text-amber-900 px-1 rounded mx-0.5 font-medium border-b-2 border-amber-300',
                     pitfall: 'bg-rose-100 text-rose-900 px-1 rounded mx-0.5 font-bold decoration-wavy underline decoration-rose-400',
                     result: 'bg-emerald-100 text-emerald-900 px-1 rounded mx-0.5 font-bold border-2 border-emerald-200 shadow-sm'
                   };
                   return <mark className={cn("inline-block", colors[type] || 'bg-yellow-100')} {...props} />;
                 }
              }}
            >
              {problem.stem}
            </ReactMarkdown>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white p-3 rounded border border-slate-200">
              <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">已知条件</span>
              <ul className="list-disc pl-4 space-y-1 text-sm text-slate-700">
                {problem.givens.map((g, i) => (
                  <li key={i}>
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex, rehypeRaw]}
                      components={{
                        p: ({node, ...props}) => <span {...props} />,
                        img: ({node, ...props}) => (
                          <img
                            {...props}
                            src={resolveMarkdownImageSrc(props.src)}
                            className={cn("max-w-full h-auto inline-block", (props as any).className)}
                          />
                        ),
                        mark: ({node, ...props}) => {
                          const type = (props as any)['data-type'] as string;
                          const colors: Record<string, string> = {
                            key: 'bg-amber-100 text-amber-900 px-1 rounded mx-0.5 font-medium',
                            pitfall: 'bg-rose-100 text-rose-900 px-1 rounded mx-0.5 font-bold decoration-wavy underline decoration-rose-400',
                            result: 'bg-emerald-100 text-emerald-900 px-1 rounded mx-0.5 font-bold'
                          };
                          return <mark className={cn(colors[type] || 'bg-yellow-100')} {...props} />;
                        }
                      }}
                    >
                      {g}
                    </ReactMarkdown>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>



      <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-md">
            {currentStep.step_id.replace('s', '')}
          </div>
          <h2 className="text-2xl font-bold text-slate-900">{currentStep.title}</h2>
        </div>
        
        <Card className="border-blue-100 shadow-md ring-1 ring-blue-50">
          <CardContent className="pt-6 text-lg leading-relaxed text-slate-800">
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex, rehypeRaw]}
              components={{
                img: ({node, ...props}) => (
                  <img
                    {...props}
                    src={resolveMarkdownImageSrc(props.src)}
                    className={cn("max-w-full h-auto inline-block", (props as any).className)}
                  />
                ),
                mark: ({node, ...props}) => {
                  const type = (props as any)['data-type'] as string;
                  const colors: Record<string, string> = {
                    key: 'bg-amber-100 text-amber-900 px-1 rounded mx-0.5 font-medium border-b-2 border-amber-300',
                    pitfall: 'bg-rose-100 text-rose-900 px-1 rounded mx-0.5 font-bold decoration-wavy underline decoration-rose-400',
                    result: 'bg-emerald-100 text-emerald-900 px-1 rounded mx-0.5 font-bold border-2 border-emerald-200 shadow-sm'
                  };
                  return <mark className={cn("inline-block", colors[type] || 'bg-yellow-100')} {...props} />;
                }
              }}
            >
              {stepRenderText}
            </ReactMarkdown>
            {mode === 'geometry' && stepDegradedTotal > 0 && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                当前步骤存在不可执行动作或不可渲染标记，已自动进入单步降级并保留文本讲解。
              </div>
            )}

            {currentStep.checkpoint && (
              <InteractiveCheckpoint 
                checkpoint={currentStep.checkpoint} 
                stepId={currentStep.step_id} 
              />
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
